import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { DetailsHistorieCardComponent } from '../../../../components/details-historie-card/details-historie-card.component';
import { DetailsHistoriePanelDirective } from '../../../../components/details-historie-card/details-historie-panel.directive';
import { PersonOption } from '../../../../components/person-select/person-select.component';
import { SaveToastService } from '../../../../components/save-toast/save-toast.service';
import { UebergabePanelComponent } from '../../../../components/uebergabe-panel/uebergabe-panel.component';
import {
  aktiveStufe,
  aktivSeit,
  toUebergabeStufen,
  uebergabeFehlerKey,
  uebergebenGesperrt,
} from '../../../../components/uebergabe-panel/uebergabe-kette';
import { VerlaufListeComponent } from '../../../../components/verlauf-liste/verlauf-liste.component';
import {
  DetailsHistorieStatus,
  DetailsHistorieTab,
  UebergabeEntwurf,
  UebergabeHandOff,
  VerlaufEintrag,
} from '../../../../models/details-historie.model';
import { HandoverChain } from '../../../../models/handover-chain.model';
import { TestModuleActivity } from '../../../../models/test-module.model';
import { HeaderInboxBadgeService } from '../../../../services/header-inbox-badge.service';
import { TestModuleHandoverService } from '../../../../services/test-module-handover.service';
import { TestModuleService } from '../../../../services/test-module.service';

const KEY = 'modules.form.historie.';
const VORLAGEN = ['schritte', 'reviewen', 'testdaten', 'freigeben'] as const;
const BEREITS_WEITERGEGEBEN = 'common.detailsHistorie.uebergabe.fehler.bereitsWeitergegeben';

@Component({
  selector: 'app-module-details-historie',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    DetailsHistorieCardComponent,
    DetailsHistoriePanelDirective,
    UebergabePanelComponent,
    VerlaufListeComponent,
  ],
  templateUrl: './module-details-historie.component.html',
  styleUrl: './module-details-historie.component.scss',
})
export class ModuleDetailsHistorieComponent {
  readonly projectId = input.required<string>();
  readonly moduleId = input<string | null>(null);
  readonly readonly = input<boolean>(false);
  readonly personOptions = input<PersonOption[]>([]);
  readonly currentUserId = input<string | null>(null);
  readonly notesFilled = input<boolean>(false);
  readonly chainChanged = output<void>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly moduleService = inject(TestModuleService);
  private readonly handoverService = inject(TestModuleHandoverService);
  private readonly toast = inject(SaveToastService);
  private readonly inboxBadge = inject(HeaderInboxBadgeService);
  // Abhängigkeit der übersetzten Texte, damit sie beim Sprachwechsel neu entstehen.
  private readonly sprache = toSignal(this.translate.onLangChange, { initialValue: null });

  readonly aktiverReiter = signal('');
  // Hält den Übergabe-Entwurf, weil der Reiterwechsel das Panel zerstört.
  readonly entwurf = signal<UebergabeEntwurf | null>(null);

  protected readonly kette = signal<HandoverChain | null>(null);
  protected readonly ketteStatus = signal<DetailsHistorieStatus>('bereit');
  protected readonly beschaeftigt = signal(false);
  protected readonly aktionsFehler = signal<string | null>(null);
  protected readonly verlaufStatus = signal<DetailsHistorieStatus>('bereit');
  private readonly aktivitaeten = signal<TestModuleActivity[]>([]);
  private ketteAbruf: Subscription | null = null;
  private verlaufAbruf: Subscription | null = null;

  protected readonly stufen = computed(() => toUebergabeStufen(this.kette()));
  protected readonly seit = computed(() => aktivSeit(this.kette()));
  private readonly inhaberId = computed(() => aktiveStufe(this.kette())?.userId ?? null);

  protected readonly auswahl = computed(() => {
    const ausgeschlossen = new Set([this.inhaberId(), this.currentUserId()]);
    return this.personOptions().filter((person) => !ausgeschlossen.has(person.id));
  });

  protected readonly sperrGrund = computed<string | null>(() => {
    this.sprache();
    return this.moduleId() ? null : this.translate.instant(`${KEY}erstNachAnlegen`);
  });

  protected readonly uebergebenSperrGrund = computed<string | null>(() => {
    this.sprache();
    return uebergebenGesperrt(this.kette(), this.inhaberId())
      ? this.translate.instant(`${KEY}bereitsWeitergegeben`)
      : null;
  });

  protected readonly vorlagen = computed<string[]>(() => {
    this.sprache();
    return VORLAGEN.map((key) => this.translate.instant(`${KEY}vorlagen.${key}`));
  });

  protected readonly verlaufEintraege = computed<VerlaufEintrag[]>(() => {
    this.sprache();
    return this.aktivitaeten().map((aktivitaet) => ({
      personName: aktivitaet.actorName ?? '',
      ereignis: this.translate.instant(`${KEY}ereignis.${aktivitaet.type}`),
      detail: aktivitaet.detail,
      zeit: aktivitaet.createdAt,
    }));
  });

  protected readonly reiter = computed<DetailsHistorieTab[]>(() => {
    this.sprache();
    const label = (id: string): string => this.translate.instant(`${KEY}reiter.${id}`);
    return [
      { id: 'uebergabe', label: label('uebergabe'), count: this.kette()?.stages.length || null },
      { id: 'verlauf', label: label('verlauf'), count: this.aktivitaeten().length || null },
      { id: 'notizen', label: label('notizen'), count: this.notesFilled() ? 1 : null },
    ];
  });

  constructor() {
    effect(() => {
      const id = this.moduleId();
      untracked(() => {
        this.kette.set(null);
        this.entwurf.set(null);
        this.aktionsFehler.set(null);
        this.aktivitaeten.set([]);
        if (!id) return;
        this.ladeKette();
        this.ladeVerlauf();
      });
    });
  }

  ladeKette(): void {
    const id = this.moduleId();
    if (!id) return;
    this.ketteAbruf?.unsubscribe();
    this.ketteStatus.set('laedt');
    this.ketteAbruf = this.handoverService
      .list(this.projectId(), id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (kette) => {
          this.kette.set(kette);
          this.ketteStatus.set('bereit');
        },
        error: () => this.ketteStatus.set('fehler'),
      });
  }

  ladeVerlauf(): void {
    const id = this.moduleId();
    if (!id) return;
    this.verlaufAbruf?.unsubscribe();
    this.verlaufStatus.set('laedt');
    this.verlaufAbruf = this.moduleService
      .getActivity(this.projectId(), id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (aktivitaeten) => {
          this.aktivitaeten.set(aktivitaeten);
          this.verlaufStatus.set('bereit');
        },
        error: () => this.verlaufStatus.set('fehler'),
      });
  }

  protected uebergeben(handOff: UebergabeHandOff): void {
    const name = this.personOptions().find((person) => person.id === handOff.userId)?.displayName ?? '';
    this.fuehreAus(
      (projectId, id) =>
        this.handoverService.handOff(projectId, id, {
          userId: handOff.userId,
          message: handOff.message ?? '',
          notify: handOff.notify,
        }),
      true,
      () => {
        this.toast.show(this.translate.instant(`${KEY}uebergebenAn`, { name }));
        this.inboxBadge.refresh();
      },
    );
  }

  protected entwurfGeaendert(entwurf: UebergabeEntwurf | null): void {
    this.entwurf.set(entwurf);
    if (!entwurf) this.aktionsFehler.set(null);
  }

  protected abschliessen(): void {
    this.fuehreAus((projectId, id) => this.handoverService.close(projectId, id), true);
  }

  protected wiederOeffnen(): void {
    this.fuehreAus((projectId, id) => this.handoverService.reopen(projectId, id), true);
  }

  protected ueberspringen(stageId: string): void {
    this.fuehreAus((projectId, id) => this.handoverService.skip(projectId, id, stageId), false);
  }

  private fuehreAus(
    aufruf: (projectId: string, moduleId: string) => Observable<HandoverChain>,
    meldeAenderung: boolean,
    danach?: () => void,
  ): void {
    const id = this.moduleId();
    if (!id || this.readonly() || this.beschaeftigt()) return;
    this.beschaeftigt.set(true);
    aufruf(this.projectId(), id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (kette) => {
          this.kette.set(kette);
          this.beschaeftigt.set(false);
          this.aktionsFehler.set(null);
          danach?.();
          this.ladeVerlauf();
          if (meldeAenderung) this.chainChanged.emit();
        },
        error: (err: HttpErrorResponse) => {
          this.beschaeftigt.set(false);
          this.aktionsFehler.set(this.fehlerText(err));
          // Sonst hielte das Panel eine später geladene fremde Stufe für das eigene Ergebnis und leerte den Entwurf.
          this.entwurf.update((entwurf) => entwurf && { ...entwurf, stufenBeimAbsenden: null });
        },
      });
  }

  // Der gemeinsame Text lautet „diesen {{objekt}}“ und passt nicht zum Neutrum „Modul“.
  private fehlerText(err: HttpErrorResponse): string {
    const key = uebergabeFehlerKey(err);
    return this.translate.instant(key === BEREITS_WEITERGEGEBEN ? `${KEY}bereitsWeitergegeben` : key);
  }
}
